if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');
        this.selling_plan_ = false;
        this.productInfo = this.closest('product-info');
        this.handleSellingPlanChange = this.onSellingPlanChange.bind(this);
        this.handleVariantChange = this.onVariantChange.bind(this);

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.productInfo?.addEventListener('change', this.handleSellingPlanChange);
        this.productInfo?.addEventListener('input', this.handleSellingPlanChange);
        this.productInfo?.addEventListener('click', this.handleSellingPlanChange);
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.handleVariantChange);

        this.sellingPlanObserver = new MutationObserver(() => this.schedulePurchaseStateSync());
        if (this.productInfo) {
          this.sellingPlanObserver.observe(this.productInfo, {
            subtree: true,
            childList: true,
            attributes: true,
            attributeFilter: ['checked', 'selected', 'value', 'aria-checked', 'aria-selected', 'data-selected'],
          });
        }

        this.syncPurchaseState();
        [50, 200, 500, 1000].forEach((delay) => setTimeout(() => this.syncPurchaseState(), delay));
      }

      disconnectedCallback() {
        this.productInfo?.removeEventListener('change', this.handleSellingPlanChange);
        this.productInfo?.removeEventListener('input', this.handleSellingPlanChange);
        this.productInfo?.removeEventListener('click', this.handleSellingPlanChange);
        this.sellingPlanObserver?.disconnect();
        this.variantChangeUnsubscriber?.();
      }

      schedulePurchaseStateSync() {
        window.clearTimeout(this.purchaseStateSyncTimer);
        this.purchaseStateSyncTimer = window.setTimeout(() => this.syncPurchaseState(), 0);
      }

      isSellingPlanControl(target) {
        if (!target) return false;
        return Boolean(
          target.matches?.('[name="selling_plan"], [data-selling-plan-id]') ||
            target.closest?.('[name="selling_plan"], [data-selling-plan-id]')
        );
      }

      onSellingPlanChange(event) {
        if (!this.isSellingPlanControl(event.target)) return;

        queueMicrotask(() => {
          this.handleErrorMessage();
          this.syncPurchaseState();
        });
      }

      onVariantChange({ data }) {
        if (!data || String(data.sectionId) !== String(this.dataset.sectionId)) return;

        const sourceForm = data.html?.querySelector('product-form');
        if (!sourceForm) return;

        ['rentalTemplate', 'requiresSellingPlan', 'validSellingPlanIds', 'variantAvailable'].forEach((key) => {
          this.dataset[key] = sourceForm.dataset[key] || '';
        });

        const sourceSubmitButton = sourceForm.querySelector('[type="submit"]');
        if (sourceSubmitButton) {
          this.submitButton.className = sourceSubmitButton.className;
        }

        const currentDynamicCheckout = this.querySelector(`#DynamicCheckout-${this.dataset.sectionId}`);
        const sourceDynamicCheckout = sourceForm.querySelector(`#DynamicCheckout-${sourceForm.dataset.sectionId}`);

        if (currentDynamicCheckout && sourceDynamicCheckout) {
          currentDynamicCheckout.innerHTML = sourceDynamicCheckout.innerHTML;
          currentDynamicCheckout.classList.toggle('hidden', sourceDynamicCheckout.classList.contains('hidden'));

          if (!currentDynamicCheckout.classList.contains('hidden') && currentDynamicCheckout.innerHTML.trim()) {
            window.Shopify?.PaymentButton?.init();
          }
        }

        this.handleErrorMessage();
        this.syncPurchaseState();
      }

      onSubmitHandler(evt) {
        evt.preventDefault();

        if (!this.isVariantAvailable()) {
          this.syncPurchaseState();
          return;
        }

        const selectedSellingPlanId = this.getSelectedSellingPlanId();
        if (this.isRentalPlanRequired() && !this.hasValidSellingPlan()) {
          this.handleErrorMessage('Bitte zuerst ein Mietmodell auswählen.');
          this.syncPurchaseState();
          return;
        }

        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();
        this.selling_plan_ = false;

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner')?.classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (selectedSellingPlanId) {
          formData.set('selling_plan', selectedSellingPlanId);
        }

        const sellingPlanId = formData.get('selling_plan');
        const timestamp = Date.now();
        let productAddedSuccessfully = false;

        if (sellingPlanId) {
          this.selling_plan_ = true;
          formData.append('properties[_timestamp]', timestamp);
          formData.append('properties[_QtyCheck]', timestamp);
        }

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then(async (httpResponse) => {
            const response = await httpResponse.json();
            if (!httpResponse.ok && !response.status) response.status = httpResponse.status;
            return response;
          })
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description || response.message);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (soldOutMessage) {
                this.submitButton.setAttribute('aria-disabled', true);
                this.submitButtonText?.classList.add('hidden');
                soldOutMessage.classList.remove('hidden');
              }
              this.error = true;
              this.selling_plan_ = false;
              return;
            }

            productAddedSuccessfully = true;

            if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');

            if (!this.error) {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            }

            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure('add:paint-updated-sections', () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure('add:paint-updated-sections', () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((error) => {
            console.error('Product add failed:', error);
            this.error = true;
            this.selling_plan_ = false;
            this.handleErrorMessage('Das Produkt konnte nicht hinzugefügt werden. Bitte versuche es erneut.');
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && productAddedSuccessfully && this.cart.classList.contains('is-empty')) {
              this.cart.classList.remove('is-empty');
            }
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner')?.classList.add('hidden');

            CartPerformance.measureFromEvent('add:user-action', evt);

            if (this.selling_plan_ && productAddedSuccessfully) {
              custom_deposite_add(timestamp).catch((error) => {
                this.handleErrorMessage(error?.message || 'Die Kaution konnte nicht hinzugefügt werden.');
              });
            }

            this.syncPurchaseState();
          });
      }

      normalizeSellingPlanId(value) {
        const rawValue = String(value || '').trim();
        if (!rawValue) return '';

        const numericId = rawValue.match(/(\d+)$/);
        return numericId ? numericId[1] : rawValue;
      }

      getSelectedSellingPlanId() {
        const container = this.productInfo || this;

        const checkedInput = container.querySelector('input[name="selling_plan"]:checked');
        if (checkedInput?.value) return this.normalizeSellingPlanId(checkedInput.value);

        const selectedOption = container.querySelector('select[name="selling_plan"]');
        if (selectedOption?.value) return this.normalizeSellingPlanId(selectedOption.value);

        const activePlan = container.querySelector(
          '[data-selling-plan-id][aria-checked="true"], [data-selling-plan-id][aria-selected="true"], [data-selling-plan-id][data-selected="true"], [data-selling-plan-id][data-active="true"], [data-selling-plan-id].selected, [data-selling-plan-id].active'
        );
        if (activePlan?.dataset?.sellingPlanId) {
          return this.normalizeSellingPlanId(activePlan.dataset.sellingPlanId);
        }

        const namedPlanInputs = Array.from(container.querySelectorAll('[name="selling_plan"]'));
        const populatedPlanInput = namedPlanInputs.find((input) => {
          if (!input.value) return false;
          if ((input.type === 'radio' || input.type === 'checkbox') && !input.checked) return false;
          return true;
        });

        return this.normalizeSellingPlanId(populatedPlanInput?.value);
      }

      getValidSellingPlanIds() {
        return (this.dataset.validSellingPlanIds || '')
          .split(',')
          .map((id) => this.normalizeSellingPlanId(id))
          .filter(Boolean);
      }

      isRentalPlanRequired() {
        return this.dataset.rentalTemplate === 'true';
      }

      isVariantAvailable() {
        return this.dataset.variantAvailable === 'true';
      }

      hasValidSellingPlan() {
        const selectedPlanId = this.getSelectedSellingPlanId();
        if (!selectedPlanId) return false;

        const validSellingPlanIds = this.getValidSellingPlanIds();
        return validSellingPlanIds.length === 0 || validSellingPlanIds.includes(selectedPlanId);
      }

      syncPurchaseState() {
        if (!this.submitButton) return;

        if (this.variantIdInput) {
          this.variantIdInput.disabled = !this.isVariantAvailable();
        }

        if (!this.isVariantAvailable()) {
          this.setSubmitState(true, window.variantStrings.soldOut);
          return;
        }

        if (this.isRentalPlanRequired() && !this.hasValidSellingPlan()) {
          this.setSubmitState(true, 'Mietmodell auswählen');
          return;
        }

        this.setSubmitState(false, window.variantStrings.addToCart);
      }

      setSubmitState(disabled, text) {
        const textChange = this.submitButton.querySelector('.addtocart_text');

        this.submitButton.toggleAttribute('disabled', disabled);
        if (disabled) {
          this.submitButton.setAttribute('aria-disabled', 'true');
        } else {
          this.submitButton.removeAttribute('aria-disabled');
        }

        if (text && textChange) textChange.textContent = text;
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        this.setSubmitState(disable, text || window.variantStrings.addToCart);
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
